import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, apiDownload, apiPost, saveBlob } from '../../lib/api'
import { InlineError } from '../InlineError'

// PO export (docs/BossMultiPO.md §4.6), legacy File → Export to Excel. A Direct-to-Store PO is
// emailed to the signed-in user, plus any addresses they type (never prefilled with the supplier's),
// or downloaded; a Group PO is downloaded with
// a sheet per store.

type ExportInfo = {
    poNumber: string; isGroup: boolean; isRevision: boolean; restricted: boolean
    caption: string; userEmail: string
}

/** Splits the typed addresses ("a@x.ca; b@y.com" — commas and spaces work too). */
export function splitRecipients(text: string): string[] {
    return text.split(/[;,\s]+/).map(s => s.trim()).filter(Boolean)
}

export default function ExportDialog({ token, store, poGuid, writesEnabled, onClose }: {
    token: string | null; store: string; poGuid: string; writesEnabled: boolean; onClose: () => void
}) {
    const infoQuery = useQuery<ExportInfo>({
        queryKey: ['multipo-export', store, poGuid],
        queryFn: () => api(`/api/purchase-orders/multi/pos/${poGuid}/export?store=${store}`, token),
        gcTime: 0,
    })
    const info = infoQuery.data

    const [cc, setCc] = useState('')
    const [notes, setNotes] = useState('')
    const [busy, setBusy] = useState<'download' | 'email' | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [sent, setSent] = useState<string | null>(null)

    const download = async () => {
        setBusy('download')
        setError(null)
        try {
            const { blob, fileName } = await apiDownload(`/api/purchase-orders/multi/pos/${poGuid}/export`, token,
                { store, action: 'download' })
            saveBlob(blob, fileName ?? `${info?.caption ?? 'PO'}.xlsx`)
        } catch (err) {
            setError(errorText(err) ?? 'The export failed.')
        } finally {
            setBusy(null)
        }
    }

    const send = async () => {
        setBusy('email')
        setError(null)
        try {
            const res: { revision: number | null; recipients: string[] } = await apiPost(
                `/api/purchase-orders/multi/pos/${poGuid}/export`, token,
                { store, action: 'email', notes, cc: splitRecipients(cc) })
            setSent(`Sent to ${res.recipients.join(', ')}.${res.revision ? ` Recorded as revision ${res.revision}.` : ''}`)
        } catch (err) {
            setError(errorText(err) ?? 'The email could not be sent.')
        } finally {
            setBusy(null)
        }
    }

    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 560 }}>
                <div className="modal-header">
                    <h2>Export PO</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modal-body">
                    {infoQuery.isLoading && <p>Loading the PO…</p>}
                    {infoQuery.isError && <InlineError message={errorText(infoQuery.error) ?? 'Failed to load the PO.'} />}
                    {info && info.restricted && <InlineError message="This PO's store is restricted. Contact head office to export it." />}
                    {info && !info.restricted && (info.isGroup ? (
                        <p className="multipo-dialog-note">
                            Downloads <strong>{info.poNumber}</strong> as an Excel file: the whole order on the first sheet,
                            then a packing slip for each store. Not for the supplier.
                        </p>
                    ) : sent ? (
                        <p className="multipo-dialog-note">{sent}</p>
                    ) : (
                        <>
                            <p className="multipo-dialog-note">
                                Emails <strong>{info.poNumber}</strong> to you ({info.userEmail || 'no address on your account'})
                                as an Excel file{info.isRevision ? ', marked as a revision with the changes since it was first sent' : ''}.
                                Sending it records a revision.
                            </p>
                            <div className="form-field">
                                <label className="form-label">Also send to</label>
                                <input className="form-input" value={cc} onChange={e => setCc(e.target.value)}
                                    placeholder="Optional: other addresses, separated by semicolons" />
                            </div>
                            <div className="form-field">
                                <label className="form-label">Subject</label>
                                <div className="multipo-dialog-static">{info.caption}</div>
                            </div>
                            <div className="form-field">
                                <label className="form-label">Notes</label>
                                <textarea className="form-textarea" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
                            </div>
                            {!writesEnabled && (
                                <p className="multipo-dialog-note">Sending needs the PO writer login, which is not set up yet. Download works.</p>
                            )}
                        </>
                    ))}
                    {error && <InlineError message={error} style={{ marginTop: 'var(--space-3)' }} />}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>{sent ? 'Close' : 'Cancel'}</button>
                    {info && !info.restricted && !sent && (
                        <button className={`btn ${info.isGroup ? 'btn-primary' : 'btn-secondary'}`} disabled={busy !== null} onClick={download}>
                            {busy === 'download' ? 'Preparing…' : 'Download'}
                        </button>
                    )}
                    {info && !info.restricted && !info.isGroup && !sent && (
                        <button className="btn btn-primary" disabled={busy !== null || !writesEnabled || !info.userEmail} onClick={send}>
                            {busy === 'email' ? 'Sending…' : 'Email it to me'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function errorText(err: unknown): string | null {
    if (!(err instanceof Error)) return null
    try {
        return JSON.parse(err.message)?.error ?? null
    } catch {
        return err.message || null
    }
}
